import { useEffect, useMemo, useState } from "react";
import { SOS_LOGO_DARK_URL } from "@/components/Logo";
import { formatMoney, formatDate } from "@/lib/format";
import { useAuthedImage } from "@/lib/useAuthedImage";

/**
 * Multi-page proposal PDF deck.
 *
 * Presentational component only — the caller is responsible for
 * fetching the `quote` + `settings` payload (see `pages/QuotePrint.jsx`
 * for the authed print flow and `pages/PublicProposal.jsx` for the
 * anonymous share-link flow). Both paths render the exact same deck so
 * a client's shared link looks identical to the salesperson's Save-as-
 * PDF export.
 *
 * Structure:
 *   1. Cover — vessel render if available, otherwise logo-driven
 *   2. Introduction letter (per-client, first-name)
 *   3. About SOS (from settings, quote can override)
 *   4. The Project — vessel render + optional GA preview
 *   5. The Quote — pricing tables with vertical blue sidebar
 *   6. The Details — Payment terms + Inclusions + Exclusions (tick + custom)
 *   7. General Arrangement page (skipped if no GA attached)
 *
 * Each page is a fixed 210×297 mm A4 sheet. `page-break-after: always`
 * on the outer <section> forces the browser's print dialog to render
 * one section per page.
 */
export default function ProposalDeck({ quote, settings }) {
  // Remove the Emergent platform badge from the DOM whenever the print
  // view is mounted. The badge uses inline `!important` styles which
  // beat any CSS rule regardless of specificity — the only reliable
  // fix is to physically detach it. A MutationObserver handles the
  // race where the badge is injected after this component mounts.
  useEffect(() => {
    const stripBadge = () => {
      document
        .querySelectorAll('#emergent-badge, a[href*="emergent.sh"], a[href*="emergentagent.com"]')
        .forEach((el) => el.remove());
    };
    stripBadge();
    const observer = new MutationObserver(stripBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Force the printed paper surface to white. The @page margin area
  // reads the html/body background at print time; if those elements
  // rely on Tailwind's `bg-*` utilities the print engine sometimes
  // renders them transparent. Setting the property inline guarantees
  // it survives every code path.
  useEffect(() => {
    const prevHtml = document.documentElement.style.background;
    const prevBody = document.body.style.background;
    document.documentElement.style.background = "white";
    document.body.style.background = "white";
    return () => {
      document.documentElement.style.background = prevHtml;
      document.body.style.background = prevBody;
    };
  }, []);

  if (!quote) return null;

  const company = settings || {};
  const vessel = quote.vessel;
  const client = quote.client;

  // Effective proposal fields — per-quote override falls back to
  // settings default, then to a sensible built-in.
  const paymentTerms =
    (quote.payment_terms_override || "").trim() || company.default_payment_terms || "";
  const showAboutSos =
    quote.include_about_sos ?? quote.about_variant !== "yachts";
  const showYachtsPage =
    quote.include_yachts_page ?? quote.about_variant === "yachts";
  const sosBody =
    (quote.about_sos_override || "").trim() || company.default_about_sos || "";
  const termsCond =
    (quote.terms_override || "").trim() || company.default_terms_conditions || "";

  const introLetter = (quote.intro_letter || "").trim();
  const exclusionsList = [
    ...(quote.exclusions_selected || []),
    ...((quote.exclusions_custom || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)),
  ];

  const customAt = (slot) => (
    <>
      {(quote.custom_pages || [])
        .filter((p) => (p.position || "before_pricing") === slot)
        .map((p) => <CustomProposalPage key={p.id} page={p} />)}
      {(quote.attachments || [])
        .filter((a) => (a.position || "end") === slot)
        .map((a) => <AttachmentPages key={a.id || a.file_path} att={a} />)}
    </>
  );

  return (
    <>
      <PrintStyles />
      <div className="proposal-root min-h-screen py-6 print:py-0" style={{ background: "#e5e7eb" }}>
        {/* Paper-white backdrop that fills every printed page. */}
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "white",
            zIndex: -1000,
          }}
        />
        <CoverPage quote={quote} vessel={vessel} company={company} />
        {customAt("after_cover")}
        {quote.include_letter_page !== false && introLetter && (
          <LetterPage
            quote={quote}
            client={client}
            company={company}
            letter={introLetter}
          />
        )}
        {customAt("after_letter")}
        {showAboutSos && sosBody && (
          <AboutPage aboutSos={sosBody} company={company} variant="sos" />
        )}
        {showYachtsPage && (
          <AboutPage aboutSos={YACHTS_ABOUT_TEXT} company={company} variant="yachts" />
        )}
        {quote.include_shipyard_page && <ShipyardPage />}
        {customAt("after_about")}
        {quote.include_options_page !== false && (quote.vessel_options?.length || 0) > 0 && (
          <OptionsPage quote={quote} />
        )}
        {quote.include_project_page !== false && vessel && (
          <ProjectPage vessel={vessel} quote={quote} />
        )}
        {customAt("before_pricing")}
        {quote.include_pricing_page !== false && (
          <QuotePage quote={quote} client={client} company={company} />
        )}
        {customAt("after_pricing")}
        {quote.include_details_page !== false && (
          <DetailsPage
            paymentTerms={paymentTerms}
            termsCond={termsCond}
            exclusionsList={exclusionsList}
            quote={quote}
            company={company}
          />
        )}
        {customAt("before_ga")}
        {quote.include_renders_page && <RendersPage vessel={vessel} />}
        {/* Dedicated GA page — quote-level upload wins; falls back to the
            linked vessel's GA. Skipped entirely when neither is set so
            the deck ends cleanly on the Details page. */}
        {quote.include_ga_page !== false && (
          <GaPage
            gaPath={quote.ga_preview_path || quote.ga_path || vessel?.ga_preview_path}
            vessel={vessel}
          />
        )}
        {/* One GA page per option vessel — same format as the main GA
            page, deduped against the main GA and each other. */}
        {quote.include_ga_page !== false &&
          (quote.vessel_options || [])
            .filter(
              (o, i, arr) =>
                o.ga_preview_path &&
                o.ga_preview_path !==
                  (quote.ga_preview_path || quote.ga_path || vessel?.ga_preview_path) &&
                arr.findIndex((x) => x.ga_preview_path === o.ga_preview_path) === i,
            )
            .map((o) => (
              <GaPage key={`ga-${o.id}`} gaPath={o.ga_preview_path} vessel={{ name: o.label }} />
            ))}
        {customAt("end")}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Custom page — free-form content in the standard deck style. Paragraphs
// split on blank lines; single line-breaks preserved (for simple lists).
// ---------------------------------------------------------------------------
// Attachment pages — original document pages rendered UNCHANGED, one full
// A4 page per PDF page (or the image itself), no SOS framing.
function AttachmentPage({ path, alt }) {
  const art = useAuthedImage(path || "");
  return (
    <section className="page" data-testid="attachment-page">
      <div className="h-full w-full flex items-center justify-center bg-white">
        {art.src ? (
          <img src={art.src} alt={alt} className="max-w-full max-h-full object-contain" />
        ) : null}
      </div>
    </section>
  );
}

function AttachmentPages({ att }) {
  const pages =
    (att.preview_paths || []).length > 0
      ? att.preview_paths
      : [att.file_path].filter(Boolean);
  return pages.map((p, i) => (
    <AttachmentPage key={p || i} path={p} alt={`${att.title || "Attachment"} — page ${i + 1}`} />
  ));
}

function CustomPageArt({ path, alt }) {
  const art = useAuthedImage(path || "");
  if (!art.src) return null;
  return (
    <div className="mt-6 flex-1 min-h-0 flex items-start justify-center">
      <img
        src={art.src}
        alt={alt}
        className="max-w-full max-h-full object-contain rounded border border-gray-200 shadow-sm"
        data-testid="custom-page-image"
      />
    </div>
  );
}

function BlockImage({ path, alt }) {
  const art = useAuthedImage(path || "");
  if (!art.src) return null;
  return (
    <div className="flex justify-center py-1">
      <img
        src={art.src}
        alt={alt}
        className="max-w-full max-h-[300px] object-contain rounded border border-gray-200 shadow-sm"
        data-testid="custom-page-block-image"
      />
    </div>
  );
}

// Inline **bold** / *italic* markers in text blocks.
function RichText({ text }) {
  const segs = (text || "").split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return segs.map((s, i) => {
    if (/^\*\*[^*]+\*\*$/.test(s))
      return (
        <strong key={i} className="font-semibold text-gray-900">
          {s.slice(2, -2)}
        </strong>
      );
    if (/^\*[^*\n]+\*$/.test(s)) return <em key={i}>{s.slice(1, -1)}</em>;
    return <span key={i}>{s}</span>;
  });
}

// Content-aware column widths: each column gets a share proportional to its
// longest cell (clamped), so short columns (e.g. "Timing") stay narrow and
// text-heavy columns get the space — fewer wrapped lines, shorter tables.
function tableColWidths(rows) {
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const scores = Array.from({ length: cols }, (_, c) => {
    let m = 0;
    for (const r of rows) m = Math.max(m, ((r[c] || "").toString() || "").length);
    return Math.max(6, Math.min(m, 60));
  });
  const total = scores.reduce((a, b) => a + b, 0) || 1;
  const minPct = 10;
  const free = 100 - minPct * cols;
  return scores.map((s) => minPct + (s / total) * free);
}

function ContentBlock({ block, pageTitle }) {
  if (block.type === "pagebreak") return null;
  if (block.type === "image") return <BlockImage path={block.path} alt={pageTitle} />;
  if (block.type === "table") {
    const rows = block.rows || [];
    const [head, ...body] = rows;
    const widths = tableColWidths(rows);
    return (
      <div
        className="overflow-hidden rounded border border-gray-200"
        data-testid="custom-page-block-table"
      >
        <table className="w-full table-fixed text-[10.5px] leading-snug text-gray-800 border-collapse">
          <colgroup>
            {widths.map((w, i) => (
              <col key={i} style={{ width: `${w}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-[#1E9CD7]/10">
              {(head || []).map((c, i) => (
                <th
                  key={i}
                  className="text-left align-top px-2 py-1.5 font-semibold text-[#0d4863] border-b border-[#1E9CD7]/30 whitespace-pre-line break-words"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((r, ri) => (
              <tr key={ri} className={ri % 2 ? "bg-gray-50" : ""}>
                {r.map((c, ci) => (
                  <td
                    key={ci}
                    className="align-top px-2 py-1 border-b border-gray-100 whitespace-pre-line break-words"
                  >
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === "heading")
    return (
      <h3
        className="text-[15px] font-semibold text-gray-900 tracking-wide pt-2 max-w-[160mm]"
        data-testid="custom-page-block-heading"
      >
        {block.text}
      </h3>
    );
  return (
    <p
      className="text-[13px] leading-relaxed text-gray-800 whitespace-pre-line max-w-[160mm]"
      data-testid="custom-page-block-text"
    >
      <RichText text={block.text} />
    </p>
  );
}

// Per-block height estimates (px inside a 96dpi A4 content area) used to
// chunk native content blocks across print pages. Calibrated against the
// real rendered sizes so pages fill properly without overflowing.
function blockCost(b) {
  if (b.type === "image") return 312;
  if (b.type === "heading") return 42;
  if (b.type === "table") {
    const rows = b.rows || [];
    // Mirror the rendered content-aware column widths (666px table span,
    // ~5.2px/char at 10.5px font).
    const widths = tableColWidths(rows);
    const charsPer = widths.map((w) => Math.max(6, Math.round((666 * w) / 100 / 5.2)));
    let h = 14;
    for (const r of rows) {
      const lines = Math.max(
        1,
        ...r.map((c, ci) => Math.ceil(((c || "").length || 1) / (charsPer[ci] || 20))),
      );
      h += lines * 13.5 + 9;
    }
    return h;
  }
  const lines = (b.text || "")
    .split("\n")
    .reduce((n, l) => n + Math.max(1, Math.ceil((l.length || 1) / 93)), 0);
  return lines * 20 + 12;
}

function splitBlock(b) {
  if (b.type === "table") {
    const rows = b.rows || [];
    const MAX = 20;
    if (rows.length <= MAX) return [b];
    const [head, ...body] = rows;
    const out = [];
    for (let i = 0; i < body.length; i += MAX - 1) {
      out.push({ type: "table", rows: [head, ...body.slice(i, i + MAX - 1)] });
    }
    return out;
  }
  return splitLongText(b);
}

function splitLongText(b) {
  const t = b.text || "";
  if (b.type !== "text" || t.length <= 1600) return [b];
  const parts = t.split(/(?<=[.!?])\s+/);
  const out = [];
  let buf = "";
  for (const p of parts) {
    if (buf && (buf + " " + p).length > 1400) {
      out.push({ type: "text", text: buf });
      buf = p;
    } else buf = buf ? buf + " " + p : p;
  }
  if (buf) out.push({ type: "text", text: buf });
  return out;
}

function paginateBlocks(blocks, firstBudget, restBudget) {
  const pages = [[]];
  let used = 0;
  let budget = firstBudget;
  for (const b of blocks) {
    if (b.type === "pagebreak") {
      // Manual page break — start a fresh page (unless already empty).
      if (pages[pages.length - 1].length > 0) {
        pages.push([]);
        used = 0;
        budget = restBudget;
      }
      continue;
    }
    const cost = blockCost(b);
    // 4% grace before forcing a break — slight overflow is absorbed by the
    // page's flex layout, while premature breaks leave ugly gaps.
    if (pages[pages.length - 1].length > 0 && used + cost > budget * 1.04) {
      pages.push([]);
      used = 0;
      budget = restBudget;
    }
    pages[pages.length - 1].push(b);
    used += cost;
  }
  // Orphan control — never leave a heading as the last block of a page.
  for (let i = 0; i < pages.length - 1; i++) {
    while (pages[i].length > 1 && pages[i][pages[i].length - 1].type === "heading") {
      pages[i + 1].unshift(pages[i].pop());
    }
  }
  return pages;
}

function CustomProposalPage({ page }) {
  const paragraphs = (page.body || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  // Native mode — extracted document content re-rendered in SOS deck style.
  if ((page.content_blocks || []).length > 0) {
    const blocks = [
      ...paragraphs.map((t) => ({ type: "text", text: t })),
      ...(page.content_blocks || []),
    ].flatMap(splitBlock);
    const deckPages = paginateBlocks(blocks, 770, 930);
    return deckPages.map((pgBlocks, pi) => (
      <section
        className="page"
        key={pi}
        data-testid={pi === 0 ? "custom-page" : "custom-page-continued"}
      >
        <div className="p-16 pt-14 h-full flex flex-col">
          {pi === 0 ? (
            <PageHeading
              eyebrow="Information"
              title={page.title || "Additional Information"}
              subtitle={page.subtitle || ""}
            />
          ) : (
            <div className="text-[10px] uppercase tracking-[0.25em] brand-primary">
              {(page.title || "Additional Information") + " — continued"}
            </div>
          )}
          <div className={`${pi === 0 ? "mt-8" : "mt-6"} space-y-3`}>
            {pgBlocks.map((b, i) => (
              <ContentBlock key={i} block={b} pageTitle={page.title || "Custom page"} />
            ))}
          </div>
          <FooterLogo />
        </div>
      </section>
    ));
  }
  const images =
    (page.image_preview_paths || []).length > 0
      ? page.image_preview_paths
      : [page.image_preview_path || page.image_path].filter(Boolean);
  return (
    <>
      <section className="page" data-testid="custom-page">
        <div className="p-16 pt-14 h-full flex flex-col">
          <PageHeading
            eyebrow="Information"
            title={page.title || "Additional Information"}
            subtitle={page.subtitle || ""}
          />
          {paragraphs.length > 0 && (
            <div className="mt-8 text-[13px] leading-relaxed text-gray-800 space-y-3 max-w-[150mm]">
              {paragraphs.map((p, i) => (
                <p key={i} className="whitespace-pre-line">
                  {p}
                </p>
              ))}
            </div>
          )}
          {images[0] && <CustomPageArt path={images[0]} alt={page.title || "Custom page"} />}
          <FooterLogo />
        </div>
      </section>
      {/* Additional PDF pages continue on their own full pages, same frame. */}
      {images.slice(1).map((img, i) => (
        <section className="page" key={img || i} data-testid="custom-page-continued">
          <div className="p-16 pt-14 h-full flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.25em] brand-primary">
              {(page.title || "Additional Information") + " — continued"}
            </div>
            <CustomPageArt path={img} alt={`${page.title || "Custom page"} ${i + 2}`} />
            <FooterLogo />
          </div>
        </section>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Print CSS — one A4 page per section, print-friendly colours.
// ---------------------------------------------------------------------------
function PrintStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@1,500&display=swap');
      /* Zero @page margins — each .page section is pre-sized to a full
         A4 sheet and provides its own internal padding for the safe
         area. Any @page margin here would double-up with the section
         padding and push content off the physical page. */
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        html, body {
          background: white !important;
          background-color: white !important;
        }
        .no-print-toolbar { display: none !important; }
        .page { box-shadow: none !important; margin: 0 !important; }
      }
      /* Hide Emergent platform badge on both the on-screen preview AND
         the printed output — the client should never see it on their
         proposal deck. Uses broad selector matching so future badge
         redesigns are covered automatically. */
      #emergent-badge,
      [id*="emergent-badge"],
      [class*="emergent-badge"],
      [class*="MadeWithEmergent"],
      [class*="made-with-emergent"],
      a[href*="emergent.sh"],
      a[href*="emergentagent.com"],
      iframe[src*="emergent"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      .page {
        width: 210mm;
        /* Slightly less than a full A4 sheet on purpose — a 297mm
           section pushes content to the very edge of the printable
           area which some print engines interpret as "already
           overflowed", triggering a spurious blank page. 296mm
           looks identical on screen but gives the printer 1mm of
           slack. */
        height: 296mm;
        background: white;
        margin: 0 auto 12px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        /* Keep every section whole; break BEFORE each subsequent
           section rather than AFTER the current one. Using
           page-break-after: always caused Chromium to emit a
           spurious blank page after every content page because the
           break directive fires even when the section already ends
           on a page boundary. Break-before is idempotent — the
           first section on a physical page never triggers an extra
           blank. */
        page-break-inside: avoid;
        break-inside: avoid;
        position: relative;
        color: #1a1a1a;
        font-family: 'Manrope', 'Inter', system-ui, sans-serif;
        overflow: hidden;
        /* Force every printed page to render its CSS backgrounds
           (the cover's dark-blue fill + gradient overlay, the letter's
           accents, etc.). Without this Chromium strips all background
           colours/images from print output, producing white pages
           where the cover should be dark blue. */
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      /* Every .page after the first one starts a new physical page.
         The first .page inherits the print origin so it doesn't
         waste a leading blank. */
      .page + .page {
        page-break-before: always;
        break-before: page;
      }
      /* Quote page carries a vertical blue sidebar via a page-level
         gradient background so the accent bar always aligns with the
         page edge regardless of internal padding. Chunking guarantees
         content fits within height:296mm, so overflow stays hidden. */
      .page.page-quote {
        background: linear-gradient(to right, #1E9CD7 0, #1E9CD7 18mm, white 18mm);
      }
      /* Keep the pricing table header + totals block on the same
         page as their surrounding content wherever possible. */
      .page.page-quote thead { display: table-header-group; }
      .page.page-quote tr { page-break-inside: avoid; }
      .page.page-quote .totals-block { page-break-inside: avoid; }

      /* Per-section corner logo — absolute-positioned inside each
         .page so it lands in the same bottom-right spot on every
         printed sheet. Beats position:fixed which Chromium does not
         reliably repeat across pages when the section is height-sized
         and the print engine paginates the deck. */
      .section-corner-logo {
        position: absolute;
        bottom: 12mm;
        right: 15mm;
        height: 10mm;
        width: auto;
        opacity: 0.85;
        z-index: 5;
        pointer-events: none;
      }

      .brand-primary { color: #1E9CD7; }
      .brand-primary-bg { background: #1E9CD7; }
      .brand-dark-bg { background: #0f2b3d; }
      .sidebar-accent { background: #1E9CD7; }
      .num { font-variant-numeric: tabular-nums; }
      .slogan-serif {
        font-family: 'Playfair Display', 'Georgia', serif;
        font-style: italic;
        letter-spacing: 0.02em;
      }
    `}</style>
  );
}

// ---------------------------------------------------------------------------
// Page 1 — Cover
// ---------------------------------------------------------------------------
function CoverPage({ quote, vessel, company }) {
  const heroPath = quote.cover_no_image
    ? ""
    : quote.cover_image_path || vessel?.render_path || vessel?.ga_preview_path;
  const hero = useAuthedImage(heroPath);
  const hasHero = !quote.cover_no_image && Boolean(hero.src);

  return (
    <section className="page page-cover" data-testid="cover-page">
      {hasHero ? (
        <>
          <img
            src={hero.src}
            alt={vessel?.name || "Vessel render"}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Blue tinted overlay for readability, mirroring the Pelco cover. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,43,61,0.35) 0%, rgba(15,43,61,0.75) 65%, rgba(15,43,61,0.95) 100%)",
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 brand-dark-bg" />
      )}

      <div className="absolute inset-0 p-16 flex flex-col justify-between text-white">
        <div>
          <img src={SOS_LOGO_DARK_URL} alt="SOS" className="h-16 w-auto brightness-0 invert" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-white/70 mb-3">
            Proposal
          </div>
          <h1
            className="font-bold uppercase leading-none tracking-tight"
            style={{ fontSize: "56px" }}
          >
            {quote.title || "Vessel Construction Proposal"}
          </h1>
          {vessel?.name && (
            <div className="mt-3 text-2xl uppercase tracking-wider text-white/90">
              {vessel.name}
              {vessel.sub_category ? ` — ${vessel.sub_category}` : ""}
            </div>
          )}
          <div className="mt-8 text-xs uppercase tracking-[0.25em] text-white/70">
            Proposed by
          </div>
          <div className="text-3xl font-bold uppercase tracking-tight mt-1">
            {company.company_name || "Southern Ocean Solutions"}
          </div>
          {quote.client && (
            <div className="mt-6 text-sm text-white/80">
              <div className="text-xs uppercase tracking-[0.25em] text-white/60">Prepared for</div>
              <div className="text-lg font-semibold text-white">
                {quote.client.company_name || quote.client.name}
              </div>
            </div>
          )}
        </div>
        <CoverFooter company={company} number={quote.number} />
      </div>
    </section>
  );
}

function CoverFooter({ company, number }) {
  const printedDate = formatDate(new Date().toISOString());
  return (
    <div className="flex items-end justify-between text-xs text-white/85">
      <div className="space-y-1 leading-snug">
        {company.address && <div>{company.address}</div>}
        {company.email && <div>{company.email}</div>}
        {company.phone && <div>{company.phone}</div>}
      </div>
      <div className="text-right">
        <div className="uppercase tracking-widest text-white/60">Quote No.</div>
        <div className="font-semibold">{number || "—"}</div>
        <div className="text-white/60 mt-1">{printedDate}</div>
      </div>
    </div>
  );
}

//

// ---------------------------------------------------------------------------
// Page 2 — Introduction letter
// ---------------------------------------------------------------------------
function LetterPage({ quote, client, company, letter }) {
  const firstName = firstNameFrom(client);
  // Signatory — the staff member picked in the editor; falls back to the
  // long-standing default.
  const signer = quote?.signatory || null;
  const signerName = signer?.name || "Michael Graham-McKenna";
  const signerEmail = signer?.email || company.email;
  const signerPhone = signer?.phone || "+61 481 347 818";
  // The AI-generated letter may already contain "Dear X," on the first
  // line. If it doesn't, prepend it so the page always opens with the
  // salutation regardless of the source.
  const opensWithDear = /^\s*dear\b/i.test(letter);
  const body = opensWithDear ? letter : `Dear ${firstName},\n\n${letter}`;

  // Long letters shrink to keep the whole letter + signature on one page.
  const estLines = body
    .split("\n")
    .reduce((n, l) => n + Math.max(1, Math.ceil((l.length || 1) / 80)), 0);
  let bodyCls = "mt-10 text-[15px] leading-relaxed";
  let compact = 0;
  if (estLines > 40) {
    bodyCls = "mt-4 text-[11px] leading-snug";
    compact = 2;
  } else if (estLines > 33) {
    bodyCls = "mt-6 text-[12.5px] leading-normal";
    compact = 2;
  } else if (estLines > 26) {
    bodyCls = "mt-8 text-[13.5px] leading-normal";
    compact = 1;
  }

  return (
    <section className="page" data-testid="letter-page">
      <div className={`p-16 ${compact === 2 ? "pt-14" : "pt-20"} flex flex-col h-full`}>
        <PageHeading
          eyebrow="Introduction"
          title="Our Proposal"
        />

        <div className={`${bodyCls} text-gray-800 whitespace-pre-line max-w-2xl`}>
          {body}
        </div>

        {/* Sign-off — professional letter closing: signature over a rule,
            then name, title/company and contact details with breathing room. */}
        <div className={compact === 2 ? "mt-5" : compact === 1 ? "mt-8" : "mt-12"} data-testid="letter-signoff">
          <div className={compact ? "text-[13px] text-gray-800" : "text-[15px] text-gray-800"}>
            Kind regards,
          </div>
          <div
            className={`${compact === 2 ? "mt-8 text-[24px]" : compact === 1 ? "mt-10 text-[28px]" : "mt-14 text-[32px]"} leading-none text-gray-900`}
            style={{ fontFamily: "'Great Vibes', cursive" }}
            data-testid="letter-signature"
          >
            {signerName}
          </div>
          <div className={`${compact === 2 ? "mt-2 pt-2" : "mt-3 pt-3"} border-t border-gray-300 w-72`}>
            <div
              className={`${compact ? "text-[13px]" : "text-[15px]"} font-semibold text-gray-900 tracking-wide`}
            >
              {signerName}
            </div>
            <div className={`${compact ? "text-[10.5px]" : "text-[12px]"} uppercase tracking-[0.18em] brand-primary mt-1`}>
              {company.company_name || "Southern Ocean Solutions"}
            </div>
            <div className={`${compact === 2 ? "mt-1" : "mt-2"} space-y-0.5 ${compact ? "text-[10.5px]" : "text-[12px]"} text-gray-600`}>
              {signerPhone && <div className="num">{signerPhone}</div>}
              <div>{signerEmail}</div>
            </div>
          </div>
        </div>

        <FooterLogo />
      </div>
    </section>
  );
}

// Honorifics to skip when deriving the client's first name — "Dr Sarah
// Jones" should open "Dear Sarah," not "Dear Dr,".
const SALUTATIONS = new Set([
  "dr", "mr", "mrs", "ms", "miss", "mx", "prof", "professor",
  "capt", "captain", "sir", "dame", "rev", "eng",
]);

function firstNameFrom(client) {
  if (!client) return "there";
  const full =
    (client.primary_contact && client.primary_contact.full_name) || client.name || "";
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  while (parts.length > 1 && SALUTATIONS.has(parts[0].toLowerCase().replace(/\.$/, ""))) {
    parts.shift();
  }
  return parts[0] || "there";
}

// ---------------------------------------------------------------------------
// Shared page heading — mirrors the reference "PRICING / THE QUOTE" style
// so every section of the proposal has the same visual hierarchy:
//   - small blue eyebrow in extended tracking
//   - big BLACK bold uppercase title, tight tracking, no extra leading
//   - optional dark subtitle in wider tracking below the title
// Kept intentionally rigid: variance across pages (different sizes,
// blue titles, extra flourishes) was making the deck feel inconsistent
// between sections.
// ---------------------------------------------------------------------------
function PageHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="border-t border-gray-300 pt-12">
      <div className="text-[10px] uppercase tracking-[0.35em] brand-primary mb-1.5 font-semibold">
        {eyebrow}
      </div>
      <div
        className="font-bold uppercase leading-none tracking-tight text-gray-900"
        style={{ fontSize: "38px" }}
      >
        {title}
      </div>
      {subtitle && (
        <div className="mt-2 text-sm uppercase tracking-widest text-gray-700">
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 3 — About SOS / About Southern Ocean Yachts
// New design (2026-07-06): dedicated 3-block layout — big header + serif
// slogan, hero image full-width, two-column body text below with room for
// a second image (`/proposal/about-sos-2.png`) if the user drops one in.
// The image files live in `/frontend/public/proposal/` so they're bundled
// with the app and don't require an authenticated fetch.
// `variant="yachts"` (per-quote tick box) swaps the heading, hero image
// and side box for the Southern Ocean Yachts version — same layout, softer voice.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Page — Vessel Options (optional)
// Subscription-style comparison: up to four vessels side by side, hero
// image on top, hand-picked specs below, price at the foot of each column.
// Rendered ahead of the detailed pricing page.
// ---------------------------------------------------------------------------
function OptionColumn({ opt, currency, altCurrencies = [] }) {
  const img = useAuthedImage(opt.render_path || "");
  return (
    <div
      className="flex flex-col rounded border border-gray-200 shadow-sm overflow-hidden"
      data-testid="option-column"
    >
      <div className="bg-white border-b border-gray-100 p-1" style={{ height: "34mm" }}>
        {img.src && (
          <img
            src={img.src}
            alt={opt.label}
            className="w-full h-full object-contain"
          />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="font-display text-[14px] font-bold tracking-wide text-gray-900 uppercase leading-snug">
          {opt.label}
        </div>
        <div className="mt-3 flex-1">
          {(opt.specs || []).map((s, i) => (
            <div
              key={i}
              className="flex justify-between gap-2 py-[3px] border-b border-gray-100 text-[10px] leading-snug"
            >
              <span className="text-gray-500 uppercase tracking-wide shrink-0">{s.label}</span>
              <span className="text-gray-900 num text-right">{s.value}</span>
            </div>
          ))}
          {(opt.features || []).length > 0 && (
            <div className="mt-3" data-testid="option-features">
              <div className="text-[9px] uppercase tracking-[0.25em] text-gray-500 mb-1.5">
                Key features
              </div>
              <div className="space-y-1">
                {opt.features.map((f, i) => (
                  <div key={i} className="flex gap-1.5 text-[10px] leading-snug text-gray-800">
                    <span className="brand-primary shrink-0">&bull;</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 pt-3 border-t-2 border-gray-800">
          <div className="text-[9px] uppercase tracking-[0.25em] text-gray-500">
            Price ({currency})
          </div>
          <div className="font-display text-[19px] font-bold brand-primary num mt-0.5">
            {opt.price != null && opt.price !== ""
              ? formatMoney(opt.price, currency)
              : "On application"}
          </div>
          {opt.price != null &&
            opt.price !== "" &&
            altCurrencies.map((a) => (
              <div key={a.currency} className="text-[10px] text-gray-500 num mt-0.5">
                approx. {formatMoney(opt.price * Number(a.rate), a.currency)}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function OptionsPage({ quote }) {
  const opts = quote.vessel_options || [];
  const altCurrencies = (quote.alt_currencies || []).filter(
    (a) => a.currency && Number(a.rate) > 0 && a.currency !== quote.currency,
  );
  return (
    <section className="page" data-testid="options-page">
      <div className="p-16 pt-14 h-full flex flex-col">
        <PageHeading
          eyebrow="Options"
          title="Vessel Options"
          subtitle="Configurations to suit your operation and budget"
        />
        <div
          className="mt-8 grid gap-4"
          style={{ gridTemplateColumns: `repeat(${Math.min(opts.length, 4)}, minmax(0, 1fr))` }}
        >
          {opts.map((opt) => (
            <OptionColumn
              key={opt.id}
              opt={opt}
              currency={quote.currency}
              altCurrencies={altCurrencies}
            />
          ))}
        </div>
        {altCurrencies.length > 0 && (
          <div className="mt-3 text-right text-[9px] text-gray-400">
            {altCurrencies
              .map((a) => `Conversion: 1 ${quote.currency} = ${a.rate} ${a.currency}`)
              .join(" · ")}
          </div>
        )}
        <FooterLogo />
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Page — About Our Shipyard (optional, per-quote tick box)
// Same 3-block format as the About pages: heading + serif subtitle, optional
// hero image slot (/proposal/about-shipyard-1.jpg), two-column body with the
// blue side box carrying the shipyard address, capabilities list and the
// closing tagline strip.
// ---------------------------------------------------------------------------
const SHIPYARD_PARAGRAPHS = [
  "Southern Ocean Solutions delivers every project through our established shipbuilding operations in Haiphong, Vietnam—one of Southeast Asia's leading centres for modern shipbuilding and heavy marine fabrication.",
  "Located within one of Vietnam's largest maritime manufacturing regions, the shipyard combines highly skilled tradespeople, modern fabrication infrastructure and efficient production processes with Australian project management, engineering oversight and quality assurance.",
  "The facility is accredited to construct projects under both Bureau Veritas (BV) and DNV classification requirements, demonstrating its capability to deliver work that meets internationally recognised standards.",
  "Purpose-built fabrication workshops, covered construction areas, CNC steel processing equipment, blasting and painting facilities, and dedicated commissioning infrastructure allow projects to be delivered efficiently while maintaining consistent quality throughout every stage of construction.",
  "Every project benefits from dedicated Australian oversight across engineering, procurement, construction, quality assurance, commissioning and sea trials, providing clients with the confidence of local project management supported by internationally competitive shipbuilding capability.",
];

const SHIPYARD_CAPABILITIES = [
  "Steel and Aluminium Vessel Construction",
  "Commercial Workboats and Specialist Vessels",
  "Luxury Yacht Construction",
  "Heavy Steel Fabrication",
  "Modular Block Construction",
  "CNC Steel Processing",
  "Mechanical Installation",
  "Electrical and Systems Integration",
  "Piping and Hydraulic Systems",
  "Blast and Paint Facilities",
  "Commissioning and Sea Trials",
  "Australian Project Management and Quality Oversight",
];

function ShipyardPage() {
  return (
    <section className="page" data-testid="shipyard-page">
      <div className="p-16 pt-14 h-full flex flex-col">
        <PageHeading
          eyebrow="Our shipyard"
          title="About Our Shipyard"
          subtitle="World-class shipbuilding from the heart of Vietnam"
        />

        {/* Hero strip — four workshop photos across the top. */}
        <div className="mt-6 grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((n) => (
            <img
              key={n}
              src={`/proposal/about-shipyard-${n}.jpg`}
              alt="SOS shipyard, Haiphong"
              className="w-full object-cover rounded border border-gray-200 shadow-sm"
              style={{ height: "42mm" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ))}
        </div>

        <div className="mt-6 grid grid-cols-5 gap-6 text-[13px] leading-relaxed text-gray-800 flex-1">
          <div className="col-span-3 space-y-3">
            {SHIPYARD_PARAGRAPHS.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="col-span-2 flex flex-col gap-4">
            {/* Shipyard address — standard blue box styling. */}
            <div className="rounded border border-[#1E9CD7]/40 bg-[#1E9CD7]/5 p-4" data-testid="shipyard-address-box">
              <div className="text-[10px] uppercase tracking-[0.25em] brand-primary mb-2">
                Our shipyard
              </div>
              <div className="text-[13px] font-semibold text-gray-900 mb-2">
                Southern Ocean Solutions
              </div>
              <div className="space-y-1 text-[11px] text-gray-700 leading-relaxed">
                <div>Hoang Mai Residential Area</div>
                <div>An Hai Ward</div>
                <div>Hai Phong City, Vietnam</div>
              </div>
            </div>
            {/* Capabilities — compact list under the address box. */}
            <div className="rounded border border-gray-200 p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">
                Shipyard capabilities
              </div>
              <div className="space-y-1 text-[11px] text-gray-700 leading-relaxed">
                {SHIPYARD_CAPABILITIES.map((c, i) => (
                  <div key={i} className="flex gap-1.5">
                    <span className="brand-primary">&bull;</span>
                    <span>{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <FooterLogo />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Built-in Southern Ocean Yachts body copy — used when the quote's About
// override is blank and the yachts variant is ticked.
const YACHTS_ABOUT_TEXT = [
  "Southern Ocean Yachts is the company behind the BEYOND Series—a family of mission-defined expedition and support yachts developed for owners whose ambitions extend beyond conventional cruising.",
  "Every BEYOND project begins by defining the mission: where the vessel will operate, how long it must remain independent, who and what it must carry, the environments it must withstand, and the capabilities it must preserve throughout its operational life. The vessel is then engineered around those requirements.",
  "The result is a different kind of yacht platform: a workboat backbone combined with superyacht standards of comfort, finish and owner experience. Practical layouts, dependable machinery, operational redundancy, maintainability and long-term support are treated as fundamental design requirements—not matters to be addressed after the styling is complete.",
  "Southern Ocean Yachts brings together experienced captains, naval architects, engineers, shipbuilders and specialist partners to take each project from mission definition and concept development through detailed engineering, construction oversight, commissioning and delivery.",
  "The BEYOND Series includes established platform designs that can be configured around a defined owner mission, as well as the capability to develop individual vessels where the operational requirement demands something entirely new.",
  "Southern Ocean Solutions collaborates with Southern Ocean Yachts as a shipbuilding and marine-services partner and may be appointed to undertake construction and associated delivery services for individual projects.",
].join("\n\n");

function AboutPage({ aboutSos, company, variant = "sos" }) {
  const isYachts = variant === "yachts";
  const paragraphs = aboutSos
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <section className="page" data-testid="about-page">
      <div className="p-16 pt-14 h-full flex flex-col">
        <PageHeading
          eyebrow={isYachts ? "Introducing" : "About us"}
          title={isYachts ? "Southern Ocean Yachts" : "About SOS"}
          subtitle={
            isYachts
              ? "MISSION-DEFINED YACHTS. ENGINEERED TO GO BEYOND."
              : "Where engineering strength meets maritime reliability"
          }
        />

        {/* Hero image — bundled in /public/proposal. Falls back to a
            subtle placeholder if the file is missing so the page still
            lays out cleanly. */}
        <div className="mt-6 rounded overflow-hidden border border-gray-200 shadow-sm">
          <img
            src={isYachts ? "/proposal/about-yachts-1.jpg" : "/proposal/about-sos-1.png"}
            alt={isYachts ? "Southern Ocean Yachts Beyond Series" : "SOS vessel"}
            className="w-full object-cover"
            style={{
              height: isYachts ? "70mm" : "80mm",
              // Bias the crop toward the lower half so the water and
              // reflection under the hull stay in frame.
              objectPosition: isYachts ? "center 85%" : "center",
            }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>

        {/* Body copy — two-column layout for the paragraphs, offset by an
            optional secondary image + side box on the right. */}
        <div
          className={`mt-6 grid grid-cols-5 gap-6 ${
            isYachts ? "text-[12px]" : "text-[13px]"
          } leading-relaxed text-gray-800 flex-1`}
        >
          <div className="col-span-3 space-y-3">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="col-span-2 flex flex-col gap-4">
            {/* Slot for a second image the user may drop into
                /public/proposal later. Hidden gracefully when absent. */}
            <img
              src={isYachts ? "/proposal/about-yachts-2.png" : "/proposal/about-sos-2.png"}
              alt={isYachts ? "Southern Ocean Yachts" : "SOS shipyard"}
              className="w-full rounded border border-gray-200 shadow-sm object-cover"
              style={{ height: "60mm" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            {isYachts ? (
              /* Southern Ocean Yachts side box — same styling as Business details. */
              <div className="rounded border border-[#1E9CD7]/40 bg-[#1E9CD7]/5 p-4" data-testid="sos-yachts-box">
                <div className="text-[13px] font-semibold brand-primary mb-2">
                  Southern Ocean Yachts
                </div>
                <div className="space-y-1 text-[11px] text-gray-700 leading-relaxed">
                  <div>The company behind the BEYOND Series</div>
                  <div>Mission-Defined Yacht Platforms</div>
                  <div className="font-semibold pt-1">
                    Expedition &nbsp;&middot;&nbsp; Support &nbsp;&middot;&nbsp; Exploration
                  </div>
                </div>
              </div>
            ) : (
              /* Business details card — always visible, sits below the
                  optional secondary image. ABN + full contact block. */
              <div className="rounded border border-[#1E9CD7]/40 bg-[#1E9CD7]/5 p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] brand-primary mb-2">
                  Business details
                </div>
                <div className="text-[13px] font-semibold text-gray-900 mb-2">
                  {company.company_name || "Southern Ocean Solutions"}
                </div>
                <div className="space-y-1 text-[11px] text-gray-700 leading-relaxed">
                  {company.abn && (
                    <div>
                      <span className="text-gray-500">ABN</span>{" "}
                      <span className="text-gray-900 num">{company.abn}</span>
                    </div>
                  )}
                  {company.address && <div>{company.address}</div>}
                  {company.phone && <div>{company.phone}</div>}
                  {company.email && <div className="break-all">{company.email}</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        <FooterLogo />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page 4 — The Project
// ---------------------------------------------------------------------------
function ProjectPage({ vessel, quote }) {
  const render = useAuthedImage(vessel?.render_path);
  const pp = (vessel?.principal_particulars || [])
    .filter((p) => p && (p.kind || "row") !== "heading")
    .filter((p) => p.enabled !== false)
    .filter((p) => (p.value || "").toString().trim());

  return (
    <section className="page" data-testid="project-page">
      <div className="p-16 h-full flex flex-col">
        <PageHeading
          eyebrow="The vessel"
          title="The Project"
          subtitle={
            vessel?.name
              ? `${vessel.name}${vessel?.sub_category ? ` · ${vessel.sub_category}` : ""}`
              : (vessel?.sub_category || vessel?.vessel_type || "Commercial Vessel")
          }
        />

        {render.src && (
          <div className="mt-6 rounded overflow-hidden border border-gray-200">
            <img
              src={render.src}
              alt={vessel?.name}
              className="w-full h-auto max-h-[110mm] object-cover"
            />
          </div>
        )}

        {pp.length > 0 && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-gray-600 mb-2">
              Principal Particulars
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[13px]">
              {pp.slice(0, 12).map((p, i) => (
                <div key={i} className="flex justify-between gap-4 border-b border-gray-200 py-1">
                  <span className="text-gray-600 shrink-0">{p.label}</span>
                  <span className="font-semibold text-gray-900 text-right">{p.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <FooterLogo />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page 5 — The Quote (pricing table with vertical blue sidebar)
// ---------------------------------------------------------------------------
function QuotePage({ quote, client, company }) {
  // Optional extras — tickable on the shared/public proposal to preview
  // an adjusted total. Never added to the actual quote total.
  const extras = (quote.optional_extras || []).filter((x) => (x.description || "").trim());
  // idx -> chosen qty (0 / absent = not selected). Clients can adjust the
  // qty on the live shared document; defaults to the qty set in the editor.
  const [pickedExtras, setPickedExtras] = useState({});
  const toggleExtra = (i) =>
    setPickedExtras((cur) => {
      const next = { ...cur };
      if (next[i]) delete next[i];
      else next[i] = Math.max(1, Number(extras[i]?.qty) || 1);
      return next;
    });
  const setExtraQty = (i, q) =>
    setPickedExtras((cur) => ({ ...cur, [i]: Math.max(1, Number(q) || 1) }));
  const pickedCount = Object.keys(pickedExtras).length;
  const pickedSum = Object.entries(pickedExtras).reduce(
    (s, [i, q]) => s + q * (Number(extras[i]?.price) || 0),
    0,
  );
  const gstMult = quote.gst_enabled ? 1 + (quote.gst_rate || 0) : 1;

  // Selectable price options — the client picks ONE; its price replaces
  // the quote total. Heading rows group the options.
  const priceOptions = (quote.price_options || []).filter((r) => (r.label || "").trim());
  const [selectedOptIdx, setSelectedOptIdx] = useState(() => {
    const d = priceOptions.findIndex((r) => (r.kind || "option") === "option" && r.is_default);
    if (d >= 0) return d;
    return priceOptions.findIndex((r) => (r.kind || "option") === "option");
  });
  const activeOption =
    selectedOptIdx >= 0 && (priceOptions[selectedOptIdx]?.kind || "option") === "option"
      ? priceOptions[selectedOptIdx]
      : null;
  const dispSubtotal = activeOption ? Number(activeOption.price) || 0 : quote.subtotal;
  const dispGst = quote.gst_enabled
    ? activeOption
      ? dispSubtotal * (quote.gst_rate || 0)
      : quote.gst
    : 0;
  const dispTotal = activeOption ? dispSubtotal + dispGst : quote.total;

  // Chunk the quote into fixed-size pages so every printed page gets
  // proper padding and a corner logo — not just the last one. Splitting
  // the DOM into multiple <section class="page">s beats relying on
  // @page margins (which the browser Print dialog can silently override
  // to "None") or position:fixed logos (which Chromium doesn't reliably
  // repeat per page in newer versions).
  // First chunk carries the full header block (client + company + meta)
  // so it fits fewer rows; continuation chunks have a slim header and
  // can fit more. Last chunk also carries the totals block so we keep
  // rowsPerPage conservative to avoid overflow.
  const rowsPerFirstPage = 18;
  const rowsPerPage = 28;

  // Flatten headings + items in order, tagging each row with its
  // section header so mid-chunk breaks still show the heading again
  // on the next physical page ("(continued)").
  // Option-comparison quotes price each vessel alternative individually —
  // they are never summed into a combined total.
  const optionPricing = (quote.vessel_options || []).filter(
    (o) => o.price != null && o.price !== "",
  );

  // Manual-rate display currencies ticked on the quote.
  const altCurrencies = (quote.alt_currencies || []).filter(
    (a) => a.currency && Number(a.rate) > 0 && a.currency !== quote.currency,
  );

  const flatRows = useMemo(() => {
    const rows = [];
    let currentHeading = null;
    for (const li of quote.line_items || []) {
      if ((li.kind || "item") === "heading") {
        currentHeading = li.description || "Section";
        rows.push({ type: "heading", label: currentHeading, li });
      } else {
        // Option-priced quotes: drop empty placeholder rows (no description,
        // zero value) so only the option pricing table shows.
        if (
          optionPricing.length > 0 &&
          !(li.description || "").trim() &&
          !(Number(li.unit_price) || 0)
        ) {
          continue;
        }
        rows.push({ type: "item", heading: currentHeading, li });
      }
    }
    if (rows.length && rows.every((r) => r.type === "heading")) return [];
    return rows;
  }, [quote.line_items, optionPricing.length]);

  // Per-heading totals — computed up-front so a subtotal band renders
  // at the point where a section actually ends, regardless of which
  // chunk it lands in.
  const sectionTotals = useMemo(() => {
    const totals = {};
    let currentHeading = null;
    for (const li of quote.line_items || []) {
      if ((li.kind || "item") === "heading") {
        currentHeading = li.description || "Section";
        totals[currentHeading] = { low: 0, high: 0 };
      } else if (currentHeading) {
        const qty = Number(li.quantity) || 0;
        const low = Number(li.unit_price) || 0;
        const high = Number(li.unit_price_high) || 0;
        totals[currentHeading] = totals[currentHeading] || { low: 0, high: 0 };
        totals[currentHeading].low += qty * low;
        totals[currentHeading].high += qty * (high > 0 ? high : low);
      }
    }
    return totals;
  }, [quote.line_items]);

  // Split flatRows into chunks. First chunk gets fewer rows (full header
  // block), later chunks get more (slim header). Last chunk still holds
  // the totals block so it's sized identically to a mid-chunk for safety.
  const chunks = useMemo(() => {
    const out = [];
    let i = 0;
    if (flatRows.length === 0) return [[]];
    // first page
    out.push(flatRows.slice(0, rowsPerFirstPage));
    i = rowsPerFirstPage;
    while (i < flatRows.length) {
      out.push(flatRows.slice(i, i + rowsPerPage));
      i += rowsPerPage;
    }
    return out;
  }, [flatRows]);

  return (
    <>
      {chunks.map((chunkRows, chunkIdx) => {
        const isFirstChunk = chunkIdx === 0;
        const isLastChunk = chunkIdx === chunks.length - 1;
        // Detect the heading in force at the START of this chunk so
        // the continuation page shows "SECTION (cont.)" if we split
        // mid-section.
        const firstRow = chunkRows[0];
        const continuedHeading =
          !isFirstChunk && firstRow?.type === "item" ? firstRow.heading : null;

        // For subtotal placement: emit a subtotal row after the LAST
        // item of a section, either where the heading changes OR at
        // the end of the deck (whichever comes first in this chunk).
        const rowsWithSubtotals = [];
        chunkRows.forEach((row, ri) => {
          rowsWithSubtotals.push(row);
          if (row.type !== "item") return;
          const nextRow = chunkRows[ri + 1];
          const isLastRowOverall =
            !nextRow &&
            (isLastChunk ||
              chunks[chunkIdx + 1]?.[0]?.type === "heading");
          const headingWillEnd = nextRow && nextRow.type === "heading";
          if ((headingWillEnd || isLastRowOverall) && row.heading) {
            rowsWithSubtotals.push({
              type: "subtotal",
              heading: row.heading,
              amount: sectionTotals[row.heading]?.low || 0,
              amountHigh:
                (sectionTotals[row.heading]?.high || 0) >
                (sectionTotals[row.heading]?.low || 0)
                  ? sectionTotals[row.heading].high
                  : 0,
            });
          }
        });

        return (
          <section
            key={chunkIdx}
            className="page page-quote"
            data-testid={isFirstChunk ? "quote-page" : `quote-page-${chunkIdx + 1}`}
          >
            <div className="pl-[32mm] pr-14 pt-16 pb-24 min-h-full flex flex-col relative">
              {isFirstChunk ? (
                <QuoteHeader quote={quote} client={client} company={company} />
              ) : (
                <QuoteContinuedHeader quote={quote} continuedHeading={continuedHeading} />
              )}

              {flatRows.length > 0 && (
                <div className="mt-4">
                <table className="w-full text-[11px] border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-800 text-[9px] uppercase tracking-widest text-gray-700">
                      <th className="text-left py-1.5 pr-3">Description</th>
                      <th className="text-right py-1.5 px-2 w-12">Qty</th>
                      <th className="text-right py-1.5 px-2 w-24 num">Unit price</th>
                      <th className="text-right py-1.5 pl-2 w-28 num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsWithSubtotals.map((row, ri) => {
                      if (row.type === "heading") {
                        return (
                          <tr key={`h-${chunkIdx}-${ri}`} className="bg-[#1E9CD7]/10">
                            <td
                              colSpan={4}
                              className="py-1.5 pr-3 pl-2 uppercase tracking-widest text-[10px] font-semibold text-[#0f5f83]"
                            >
                              {row.label}
                            </td>
                          </tr>
                        );
                      }
                      if (row.type === "subtotal") {
                        return (
                          <tr key={`sub-${chunkIdx}-${ri}`} className="border-b-2 border-gray-800">
                            <td colSpan={3} className="py-1 pr-3 pl-2 text-right text-[10px] uppercase tracking-widest text-gray-600 font-semibold">
                              {row.heading} subtotal
                            </td>
                            <td className="py-1 pl-2 text-right font-bold num text-gray-900">
                              {formatMoney(row.amount, quote.currency)}
                              {row.amountHigh > 0 &&
                                ` – ${formatMoney(row.amountHigh, quote.currency)}`}
                            </td>
                          </tr>
                        );
                      }
                      const li = row.li;
                      const amt = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
                      const liHigh = Number(li.unit_price_high) || 0;
                      const amtHigh = liHigh > 0 ? (Number(li.quantity) || 0) * liHigh : 0;
                      return (
                        <tr key={li.id || `s${chunkIdx}-${ri}`} className="border-b border-gray-200">
                          <td className="py-1 pr-3 align-top">
                            <div className="text-gray-900">{li.description}</div>
                          </td>
                          <td className="py-1 px-2 text-right num align-top">
                            {Number(li.quantity || 0).toLocaleString()}
                          </td>
                          <td className="py-1 px-2 text-right num align-top">
                            {formatMoney(li.unit_price, quote.currency)}
                            {liHigh > 0 && (
                              <div className="text-[10px] text-gray-500">
                                – {formatMoney(liHigh, quote.currency)}
                              </div>
                            )}
                          </td>
                          <td className="py-1 pl-2 text-right font-semibold num align-top">
                            {formatMoney(amt, quote.currency)}
                            {amtHigh > 0 && (
                              <div className="text-[10px] text-gray-500 font-normal">
                                – {formatMoney(amtHigh, quote.currency)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}

              {isLastChunk && optionPricing.length > 0 && (
                <div className="mt-4" data-testid="quote-page-option-pricing">
                  <table className="w-full text-[11px] border-collapse">
                    <thead>
                      <tr className="border-b-2 border-gray-800 text-[9px] uppercase tracking-widest text-gray-700">
                        <th className="text-left py-1.5 pr-3">Vessel option</th>
                        <th className="text-right py-1.5 pl-2 w-44 num">
                          Price ({quote.currency})
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {optionPricing.map((o, i) => (
                        <tr key={o.id || i} className="border-b border-gray-200">
                          <td className="py-2 pr-3 font-semibold text-gray-900 uppercase tracking-wide">
                            {o.label || `Option ${i + 1}`}
                          </td>
                          <td className="py-2 pl-2 text-right font-bold num brand-primary">
                            {formatMoney(o.price, quote.currency)}
                            {altCurrencies.map((a) => (
                              <div
                                key={a.currency}
                                className="text-[9.5px] text-gray-500 font-normal"
                              >
                                approx. {formatMoney(o.price * Number(a.rate), a.currency)}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 text-right text-[10px] text-gray-500">
                    Prices are per vessel option — only the selected option applies.
                    {altCurrencies.length > 0 &&
                      " " +
                        altCurrencies
                          .map((a) => `Conversion: 1 ${quote.currency} = ${a.rate} ${a.currency}.`)
                          .join(" ")}
                  </div>
                </div>
              )}

              {isLastChunk && priceOptions.length > 0 && (
                <div className="mt-5" data-testid="price-options-section">
                  <div className="text-[10px] uppercase tracking-[0.3em] brand-primary mb-2">
                    Select Your Option
                  </div>
                  <div className="text-[10px] text-gray-500 mb-2">
                    Choose one option below — the total updates to match your selection.
                  </div>
                  <div className="border border-gray-300 rounded overflow-hidden">
                    {priceOptions.map((row, i) =>
                      (row.kind || "option") === "heading" ? (
                        <div
                          key={i}
                          className={`px-3 py-1.5 bg-gray-100 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-semibold ${i > 0 ? "border-t border-gray-200" : ""}`}
                          data-testid={`price-option-heading-${i}`}
                        >
                          {row.label}
                        </div>
                      ) : (
                        <div
                          key={i}
                          onClick={() => setSelectedOptIdx(i)}
                          className={`flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer ${i > 0 ? "border-t border-gray-200" : ""} ${selectedOptIdx === i ? "bg-blue-50" : "bg-white"}`}
                          data-testid={`price-option-row-${i}`}
                        >
                          <input
                            type="radio"
                            checked={selectedOptIdx === i}
                            onChange={() => setSelectedOptIdx(i)}
                            className="h-3.5 w-3.5 accent-[#004AAD] shrink-0 cursor-pointer"
                            data-testid={`price-option-radio-${i}`}
                          />
                          <span className="flex-1">
                            <span className={`text-gray-800 ${selectedOptIdx === i ? "font-semibold" : ""}`}>
                              {row.label}
                            </span>
                            {row.description ? (
                              <span className="block text-[10px] text-gray-500">{row.description}</span>
                            ) : null}
                          </span>
                          <span
                            className={`num w-32 text-right font-medium ${selectedOptIdx === i ? "brand-primary font-bold" : "text-gray-800"}`}
                          >
                            {formatMoney(Number(row.price) || 0, quote.currency)}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {isLastChunk && optionPricing.length === 0 && (
                <div className="mt-5 flex justify-end totals-block">
                  <div className="w-72 text-xs">
                    {quote.has_range ? (
                      <>
                        <div className="flex justify-between items-baseline border-t-2 border-gray-800 mt-1.5 pt-1.5 text-base font-bold">
                          <span>Estimated Total ({quote.currency})</span>
                        </div>
                        <div className="text-right num brand-primary text-base font-bold mt-0.5">
                          {formatMoney(quote.subtotal, quote.currency)} –{" "}
                          {formatMoney(quote.subtotal_high, quote.currency)}
                        </div>
                        <div className="text-right text-[10px] text-gray-500 mt-1">
                          Indicative estimate — excludes GST
                        </div>
                        {altCurrencies.map((a) => (
                          <div key={a.currency} className="text-right text-[11px] text-gray-600 num mt-0.5">
                            approx. {formatMoney(quote.subtotal * Number(a.rate), a.currency)} –{" "}
                            {formatMoney(quote.subtotal_high * Number(a.rate), a.currency)}
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between py-0.5 text-gray-700">
                          <span>Subtotal</span>
                          <span className="num">{formatMoney(dispSubtotal, quote.currency)}</span>
                        </div>
                        {quote.gst_enabled && (
                          <div className="flex justify-between py-0.5 text-gray-700">
                            <span>GST ({Math.round((quote.gst_rate || 0) * 100)}%)</span>
                            <span className="num">{formatMoney(dispGst, quote.currency)}</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t-2 border-gray-800 mt-1.5 pt-1.5 text-base font-bold">
                          <span>Total ({quote.currency})</span>
                          <span className="num brand-primary">
                            {formatMoney(dispTotal, quote.currency)}
                          </span>
                        </div>
                        {altCurrencies.map((a) => (
                          <div
                            key={a.currency}
                            className="flex justify-between py-0.5 text-[11px] text-gray-600"
                            data-testid={`quote-alt-total-${a.currency}`}
                          >
                            <span>approx. {a.currency}</span>
                            <span className="num">
                              {formatMoney(dispTotal * Number(a.rate), a.currency)}
                            </span>
                          </div>
                        ))}
                        {altCurrencies.length > 0 && (
                          <div className="text-right text-[9px] text-gray-400 mt-1">
                            {altCurrencies
                              .map((a) => `Conversion: 1 ${quote.currency} = ${a.rate} ${a.currency}`)
                              .join(" · ")}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {isLastChunk && extras.length > 0 && (
                <div className="mt-6" data-testid="optional-extras-section">
                  <div className="text-[10px] uppercase tracking-[0.3em] brand-primary mb-2">
                    Optional Extras
                  </div>
                  <div className="text-[10px] text-gray-500 mb-2">
                    Not included in the price above — tick any (and set a quantity) to
                    preview the total with them added.
                  </div>
                  <div className="border border-gray-300 rounded">
                    {extras.map((x, i) => {
                      const qty = pickedExtras[i];
                      const unit = Number(x.price) || 0;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-2.5 px-3 py-1.5 text-xs ${i > 0 ? "border-t border-gray-200" : ""}`}
                          data-testid={`optional-extra-row-${i}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!qty}
                            onChange={() => toggleExtra(i)}
                            className="h-3.5 w-3.5 accent-[#004AAD] shrink-0 cursor-pointer"
                            data-testid={`optional-extra-check-${i}`}
                          />
                          <span className="flex-1 text-gray-700 cursor-pointer" onClick={() => toggleExtra(i)}>
                            {x.description}
                          </span>
                          {qty ? (
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              <span className="text-[10px] uppercase tracking-wide text-gray-400">Qty</span>
                              <input
                                type="number"
                                min="1"
                                value={qty}
                                onChange={(e) => setExtraQty(i, e.target.value)}
                                className="w-14 h-6 bg-white text-gray-800 border border-gray-300 rounded px-1.5 text-xs num text-right focus:outline-none focus:border-[#004AAD]"
                                data-testid={`optional-extra-qty-${i}`}
                              />
                            </span>
                          ) : (
                            (Number(x.qty) || 1) > 1 && (
                              <span className="text-[10px] text-gray-400">Qty {Number(x.qty) || 1}</span>
                            )
                          )}
                          <span className="num text-gray-800 font-medium w-28 text-right">
                            {qty > 1
                              ? `${formatMoney(unit * qty, quote.currency)}`
                              : formatMoney(unit, quote.currency)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {pickedCount > 0 && (
                    <div className="mt-2.5 flex justify-end">
                      <div className="w-80 text-xs">
                        <div className="flex justify-between py-0.5 text-gray-600">
                          <span>Selected extras{quote.gst_enabled ? " (incl. GST)" : ""}</span>
                          <span className="num">{formatMoney(pickedSum * gstMult, quote.currency)}</span>
                        </div>
                        {quote.has_range ? (
                          <div className="flex justify-between border-t border-gray-400 mt-1 pt-1 font-bold text-[13px]">
                            <span>Estimated total with extras</span>
                            <span className="num brand-primary">
                              {formatMoney(quote.subtotal + pickedSum, quote.currency)} –{" "}
                              {formatMoney(quote.subtotal_high + pickedSum, quote.currency)}
                            </span>
                          </div>
                        ) : (
                          <div
                            className="flex justify-between border-t border-gray-400 mt-1 pt-1 font-bold text-[13px]"
                            data-testid="optional-extras-adjusted-total"
                          >
                            <span>Total with selected extras ({quote.currency})</span>
                            <span className="num brand-primary">
                              {formatMoney(dispTotal + pickedSum * gstMult, quote.currency)}
                            </span>
                          </div>
                        )}
                        {!quote.has_range &&
                          altCurrencies.map((a) => (
                            <div
                              key={a.currency}
                              className="flex justify-between py-0.5 text-[11px] text-gray-600"
                              data-testid={`extras-alt-total-${a.currency}`}
                            >
                              <span>approx. {a.currency}</span>
                              <span className="num">
                                {formatMoney((dispTotal + pickedSum * gstMult) * Number(a.rate), a.currency)}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Per-page corner logo — absolute-positioned inside the
                  section so it lands in the same bottom-right spot on
                  every chunked page. */}
              <img
                src={SOS_LOGO_DARK_URL}
                alt="SOS"
                className="section-corner-logo"
                aria-hidden="true"
              />
            </div>
          </section>
        );
      })}
    </>
  );
}

function QuoteHeader({ quote, client, company }) {
  return (
    <>
      <div className="flex items-start justify-between border-b-2 border-gray-800 pb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] brand-primary mb-1.5">
            Pricing
          </div>
          <div
            className="font-bold uppercase leading-none tracking-tight"
            style={{ fontSize: "38px" }}
          >
            The Quote
          </div>
          {quote.vessel?.name && (
            <div className="mt-1.5 text-xs font-semibold text-gray-700">
              {quote.vessel.name}
              {quote.vessel.sub_category ? ` · ${quote.vessel.sub_category}` : ""}
            </div>
          )}
        </div>
        <div className="text-right text-[10px]">
          <MetaLine label="Quote No." value={quote.number} />
          <MetaLine label="Date" value={formatDate(quote.created_at)} />
          {quote.valid_until && (
            <MetaLine label="Valid until" value={formatDate(quote.valid_until)} />
          )}
          <MetaLine label="Currency" value={quote.currency} />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-5 text-[10px]">
        <div>
          <div className="uppercase tracking-widest text-gray-500">Prepared for</div>
          {client ? (
            <>
              <div className="font-semibold text-gray-900 text-xs mt-0.5">
                {client.company_name || client.name}
              </div>
              {client.primary_contact?.full_name && (
                <div className="text-gray-600">
                  Attn: {client.primary_contact.full_name}
                </div>
              )}
              {client.address && (
                <div className="text-gray-600 whitespace-pre-line">{client.address}</div>
              )}
            </>
          ) : (
            <div className="text-gray-400 mt-0.5">—</div>
          )}
        </div>
        <div>
          <div className="uppercase tracking-widest text-gray-500">From</div>
          <div className="font-semibold text-gray-900 text-xs mt-0.5">
            {company.company_name || "Southern Ocean Solutions"}
          </div>
          {company.abn && <div className="text-gray-600">ABN: {company.abn}</div>}
          {company.email && <div className="text-gray-600">{company.email}</div>}
          {company.phone && <div className="text-gray-600">{company.phone}</div>}
        </div>
      </div>
    </>
  );
}

function QuoteContinuedHeader({ quote, continuedHeading }) {
  return (
    <div className="flex items-baseline justify-between border-b border-gray-300 pb-2">
      <div>
        <div className="text-[9px] uppercase tracking-[0.35em] brand-primary">
          The Quote (continued)
        </div>
        {continuedHeading && (
          <div className="text-[10px] uppercase tracking-widest text-gray-600 mt-0.5">
            {continuedHeading} (cont.)
          </div>
        )}
      </div>
      <div className="text-right text-[9px] text-gray-500">
        Quote No. {quote.number}
      </div>
    </div>
  );
}

function MetaLine({ label, value }) {
  return (
    <div className="flex gap-3 justify-end">
      <span className="uppercase tracking-widest text-gray-500">{label}</span>
      <span className="font-semibold text-gray-900">{value || "—"}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 6 — The Details (Payment terms + Exclusions)
// ---------------------------------------------------------------------------
function DetailsPage({ paymentTerms, termsCond, exclusionsList, quote, company }) {
  const notesBullets = (quote.notes || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <section className="page" data-testid="details-page">
      <div className="p-16 h-full flex flex-col">
        <PageHeading
          eyebrow="Payment & terms"
          title="The Details"
        />

        <div className="mt-8 grid grid-cols-1 gap-6 text-[13px] leading-relaxed text-gray-800">
          {paymentTerms && (
            <div>
              <div className="font-bold uppercase tracking-widest text-[12px] mb-2 border-b border-gray-300 pb-1">
                Payment Terms
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {paymentTerms
                  .split(/\r?\n/)
                  .map((l) => l.trim())
                  .filter(Boolean)
                  .map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
              </ul>
            </div>
          )}

          {termsCond && (
            <div>
              <div className="font-bold uppercase tracking-widest text-[12px] mb-2 border-b border-gray-300 pb-1">
                General Terms &amp; Conditions
              </div>
              <div className="whitespace-pre-line">{termsCond}</div>
            </div>
          )}

          {notesBullets.length > 0 && (
            <div>
              <div className="font-bold uppercase tracking-widest text-[12px] mb-2 border-b border-gray-300 pb-1">
                Considerations
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {notesBullets.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {exclusionsList.length > 0 && (
            <div>
              <div className="font-bold uppercase tracking-widest text-[12px] mb-2 border-b border-gray-300 pb-1">
                Exclusions
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {exclusionsList.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <FooterLogo />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Final page — General Arrangement (dedicated, large-format)
// ---------------------------------------------------------------------------
function RendersPage({ vessel }) {
  const imgs = (vessel?.gallery || []).filter((g) => g?.path).slice(0, 8);
  if (imgs.length === 0) return null;
  const cols = imgs.length <= 2 ? 1 : 2;
  return (
    <section className="page" data-testid="renders-page">
      <div className="p-16 h-full flex flex-col">
        <PageHeading
          eyebrow="Imagery"
          title="Vessel Renders"
          subtitle={
            vessel?.name
              ? `${vessel.name}${vessel?.sub_category ? ` · ${vessel.sub_category}` : ""}`
              : ""
          }
        />
        <div
          className="mt-8 flex-1 grid gap-4 min-h-0"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gridAutoRows: "1fr",
          }}
        >
          {imgs.map((g, i) => (
            <RenderCell key={i} path={g.path} index={i} />
          ))}
        </div>
        <FooterLogo />
      </div>
    </section>
  );
}

function RenderCell({ path, index }) {
  const img = useAuthedImage(path || "");
  return (
    <div
      className="bg-white border border-gray-200 rounded overflow-hidden flex items-center justify-center min-h-0"
      data-testid={`renders-page-img-${index}`}
    >
      {img.src ? (
        <img src={img.src} alt="Vessel render" className="w-full h-full object-contain p-1" />
      ) : (
        <div className="text-xs text-gray-400">Loading…</div>
      )}
    </div>
  );
}

function GaPage({ gaPath, vessel }) {  const ga = useAuthedImage(gaPath || "");
  // Only render the page if we actually have a GA to show. Prevents an
  // orphan blank page at the end of the deck when neither the quote nor
  // the vessel has one attached.
  if (!gaPath) return null;
  // If the preview path is still a PDF (i.e. the server-side PNG
  // rasteriser failed at upload time — typically memory pressure on a
  // small deploy tier), fall back to an <object> embed so the client
  // at least sees the drawing instead of a broken <img>. Users can
  // click "Regenerate preview" on the Quote editor to fix it properly.
  const isPdf = /\.pdf$/i.test(gaPath);
  return (
    <section className="page" data-testid="ga-page">
      <div className="p-16 h-full flex flex-col">
        <PageHeading
          eyebrow="Drawings"
          title="General Arrangement"
          subtitle={
            vessel?.name
              ? `${vessel.name}${vessel?.sub_category ? ` · ${vessel.sub_category}` : ""}`
              : ""
          }
        />

        <div className="mt-8 flex-1 flex items-center justify-center bg-white border border-gray-200 rounded overflow-hidden">
          {isPdf && ga.src ? (
            <object
              data={ga.src}
              type="application/pdf"
              className="w-full h-full"
              style={{ minHeight: "200mm" }}
            >
              <div className="p-8 text-sm text-gray-500 text-center">
                Preview unavailable in this browser.<br />
                <a href={ga.src} className="text-[#1E9CD7] underline">Open drawing</a>
              </div>
            </object>
          ) : ga.src ? (
            <img
              src={ga.src}
              alt="General Arrangement"
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: "200mm" }}
            />
          ) : (
            <div className="text-sm text-gray-500">Loading drawing…</div>
          )}
        </div>

        <FooterLogo />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared page footer — renders the bottom-right SOS logo inside each
// non-cover section. Since each .page section is exactly A4-sized and
// paginates as its own printed sheet, absolute positioning inside the
// section gives us a reliable per-page corner mark that doesn't depend
// on the browser's inconsistent position:fixed repeat behaviour.
// ---------------------------------------------------------------------------
function FooterLogo() {
  return (
    <img
      src={SOS_LOGO_DARK_URL}
      alt="SOS"
      className="section-corner-logo"
      aria-hidden="true"
    />
  );
}
